// src/views/pages/CrmLeads.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { useCrmController } from '../../controllers/useCrmController';
import { CRM_STAGES, CRM_PRIORITIES } from '../../services/crmService';
import { formatIndianCurrency } from '../../utils/currency';
import {
  Search,
  Download,
  Phone,
  MessageCircle,
  X,
  Clock,
  CheckCircle,
  Kanban,
  List,
  Calendar,
  UserCheck,
  Flame,
  Sun,
  Snowflake,
  Send,
  Plus,
  BarChart3
} from 'lucide-react';

const CrmLeads = () => {
  const {
    loading,
    filteredLeads,
    staffList,
    todaysFollowups,
    kanbanColumns,
    viewMode,
    setViewMode,
    selectedLead,
    handleSelectLead,
    leadActivities,
    activitiesLoading,
    search,
    setSearch,
    categoryFilter,
    setCategoryFilter,
    cityFilter,
    setCityFilter,
    stageFilter,
    setStageFilter,
    priorityFilter,
    setPriorityFilter,
    assignFilter,
    setAssignFilter,
    dateFilter,
    setDateFilter,
    newNote,
    setNewNote,
    activityType,
    setActivityType,
    savingNote,
    handleAddNote,
    newFollowupTitle,
    setNewFollowupTitle,
    newFollowupDate,
    setNewFollowupDate,
    savingFollowup,
    handleCreateFollowup,
    handleCompleteFollowup,
    handleUpdateStatus,
    handleUpdatePriority,
    handleAssignLead,
    handleExportCSV,
    currentAdminProfile
  } = useCrmController();

  const getPriorityBadge = (priority) => {
    switch ((priority || '').toLowerCase()) {
      case 'hot':
        return (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '3px',
            fontSize: '11px',
            fontWeight: 700,
            padding: '2px 6px',
            borderRadius: '4px',
            backgroundColor: '#fee2e2',
            color: '#dc2626'
          }}>
            <Flame size={12} /> Hot
          </span>
        );
      case 'cold':
        return (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '3px',
            fontSize: '11px',
            fontWeight: 600,
            padding: '2px 6px',
            borderRadius: '4px',
            backgroundColor: '#e0f2fe',
            color: '#0284c7'
          }}>
            <Snowflake size={12} /> Cold
          </span>
        );
      default:
        return (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '3px',
            fontSize: '11px',
            fontWeight: 600,
            padding: '2px 6px',
            borderRadius: '4px',
            backgroundColor: '#fef3c7',
            color: '#d97706'
          }}>
            <Sun size={12} /> Warm
          </span>
        );
    }
  };

  const getCategoryBadge = (category) => {
    let bg = 'var(--brand-accent-soft)';
    let color = 'var(--brand-accent)';
    if (category === 'Solution Modal') {
      bg = 'rgba(185, 28, 28, 0.12)';
      color = '#b91c1c';
    } else if (category === 'Property Enquiry') {
      bg = 'rgba(16, 185, 129, 0.12)';
      color = '#059669';
    }

    return (
      <span style={{
        fontSize: '10px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        padding: '2px 6px',
        borderRadius: '4px',
        backgroundColor: bg,
        color: color
      }}>
        {category}
      </span>
    );
  };

  return (
    <div className="page-body">
      {/* Top Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 className="page-title">CRM & Sales Pipeline</h1>
          <p className="page-subtitle">Unified lead management for website popup enquiries, mobile app users, and service requests.</p>
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* View Switcher */}
          <div style={{ display: 'flex', backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-sm)', padding: '2px' }}>
            <button
              type="button"
              onClick={() => setViewMode('kanban')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: 600,
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                backgroundColor: viewMode === 'kanban' ? 'var(--primary)' : 'transparent',
                color: viewMode === 'kanban' ? '#fff' : 'var(--text-secondary)',
                cursor: 'pointer'
              }}
            >
              <Kanban size={14} /> Pipeline
            </button>
            <button
              type="button"
              onClick={() => setViewMode('table')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: 600,
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                backgroundColor: viewMode === 'table' ? 'var(--primary)' : 'transparent',
                color: viewMode === 'table' ? '#fff' : 'var(--text-secondary)',
                cursor: 'pointer'
              }}
            >
              <List size={14} /> Table View
            </button>
            <button
              type="button"
              onClick={() => setViewMode('followups')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: 600,
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                backgroundColor: viewMode === 'followups' ? 'var(--primary)' : 'transparent',
                color: viewMode === 'followups' ? '#fff' : 'var(--text-secondary)',
                cursor: 'pointer'
              }}
            >
              <Clock size={14} /> Follow-ups
              {todaysFollowups.length > 0 && (
                <span style={{ backgroundColor: '#ef4444', color: '#fff', fontSize: '10px', padding: '1px 5px', borderRadius: '999px' }}>
                  {todaysFollowups.length}
                </span>
              )}
            </button>
          </div>

          <Link
            to="/crm/reports"
            className="btn btn-secondary"
            style={{ gap: '6px', fontSize: '13px', textDecoration: 'none' }}
          >
            <BarChart3 size={14} /> Reports & Performance
          </Link>

          <button onClick={handleExportCSV} className="btn btn-secondary" style={{ gap: '6px', fontSize: '13px' }}>
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="filters-bar" style={{ marginBottom: '20px' }}>
        <div className="search-input-wrapper" style={{ flex: 2 }}>
          <Search size={16} className="search-icon-fixed" />
          <input
            type="text"
            placeholder="Search leads by name, phone, city, requirement, or property..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
        </div>

        {/* Category */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>Source:</span>
          <select className="filter-select" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
            <option value="all">All Sources</option>
            <option value="Solution Modal">Solution Popup</option>
            <option value="Property Enquiry">Property Enquiry (App/Web)</option>
            <option value="Service Lead">Custom Services</option>
          </select>
        </div>

        {/* City */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>City:</span>
          <select className="filter-select" value={cityFilter} onChange={(e) => setCityFilter(e.target.value)}>
            <option value="all">All Cities</option>
            <option value="Ghazipur">Ghazipur</option>
            <option value="Varanasi">Varanasi</option>
            <option value="Lucknow">Lucknow</option>
            <option value="Other">Other</option>
          </select>
        </div>

        {/* Priority */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>Priority:</span>
          <select className="filter-select" value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
            <option value="all">All Priorities</option>
            {CRM_PRIORITIES.map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>

        {/* Staff Assignment */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>Assigned:</span>
          <select className="filter-select" value={assignFilter} onChange={(e) => setAssignFilter(e.target.value)}>
            <option value="all">All Staff</option>
            <option value="mine">Assigned to Me</option>
            <option value="unassigned">Unassigned</option>
            {staffList.map(s => (
              <option key={s.id} value={s.id}>{s.full_name}</option>
            ))}
          </select>
        </div>

        {/* Date Filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>Date:</span>
          <select className="filter-select" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">Past 7 Days</option>
            <option value="month">Past 30 Days</option>
          </select>
        </div>
      </div>

      {/* Main Content Area */}
      {loading ? (
        <div className="skeleton" style={{ height: '420px', borderRadius: 'var(--radius-md)' }} />
      ) : viewMode === 'kanban' ? (
        /* ================= KANBAN PIPELINE VIEW ================= */
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: '16px',
          alignItems: 'start',
          overflowX: 'auto',
          paddingBottom: '20px'
        }}>
          {CRM_STAGES.map((stage) => {
            const columnLeads = kanbanColumns[stage.key] || [];
            return (
              <div
                key={stage.key}
                style={{
                  backgroundColor: 'var(--bg-secondary)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-primary)',
                  display: 'flex',
                  flexDirection: 'column',
                  maxHeight: 'calc(100vh - 220px)',
                  minWidth: '260px'
                }}
              >
                {/* Column Header */}
                <div style={{
                  padding: '12px 14px',
                  borderBottom: '1px solid var(--border-primary)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderTop: `3px solid ${stage.color}`
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontWeight: 700, fontSize: '13px' }}>{stage.label}</span>
                    <span style={{
                      backgroundColor: 'rgba(0,0,0,0.06)',
                      color: 'var(--text-secondary)',
                      fontSize: '11px',
                      fontWeight: 700,
                      padding: '1px 6px',
                      borderRadius: '999px'
                    }}>
                      {columnLeads.length}
                    </span>
                  </div>
                </div>

                {/* Column Cards Container */}
                <div style={{ padding: '10px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {columnLeads.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '30px 10px', color: 'var(--text-muted)', fontSize: '12px' }}>
                      No leads in this stage
                    </div>
                  ) : (
                    columnLeads.map((lead) => (
                      <div
                        key={lead.lead_id}
                        onClick={() => handleSelectLead(lead)}
                        style={{
                          backgroundColor: 'var(--bg-primary)',
                          borderRadius: 'var(--radius-sm)',
                          border: '1px solid var(--border-primary)',
                          padding: '12px',
                          cursor: 'pointer',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                          transition: 'all var(--transition-fast)'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                          <span style={{ fontWeight: 700, fontSize: '13px', color: 'var(--text-primary)' }}>
                            {lead.customer_name}
                          </span>
                          {getPriorityBadge(lead.priority)}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                          <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                            {lead.customer_phone}
                          </span>
                          {lead.customer_phone && (
                            <a
                              href={`https://wa.me/91${lead.customer_phone.replace(/\D/g, '')}`}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              style={{ color: '#25D366' }}
                              title="WhatsApp"
                            >
                              <MessageCircle size={13} />
                            </a>
                          )}
                        </div>

                        <div style={{ marginBottom: '8px' }}>
                          {getCategoryBadge(lead.lead_category)}
                          <p style={{
                            fontSize: '12px',
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            margin: '4px 0 0 0',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {lead.requirement}
                          </p>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', color: 'var(--text-muted)', borderTop: '1px solid var(--border-primary)', paddingTop: '8px' }}>
                          <span>{lead.city} {lead.budget ? `• ${formatIndianCurrency(lead.budget)}` : ''}</span>
                          <span style={{ fontWeight: 600, color: lead.assigned_to_name ? 'var(--primary)' : 'var(--text-muted)' }}>
                            {lead.assigned_to_name ? lead.assigned_to_name.split(' ')[0] : 'Unassigned'}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : viewMode === 'table' ? (
        /* ================= UNIFIED TABLE VIEW ================= */
        <div className="table-container">
          <table className="premium-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Source</th>
                <th style={{ width: '22%' }}>Requirement</th>
                <th>City & Budget</th>
                <th>Priority</th>
                <th>Assigned To</th>
                <th>Stage / Status</th>
                <th>Date</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLeads.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
                    No leads match the specified filter.
                  </td>
                </tr>
              ) : (
                filteredLeads.map((lead) => (
                  <tr key={lead.lead_id} onClick={() => handleSelectLead(lead)} style={{ cursor: 'pointer' }}>
                    <td>
                      <span style={{ fontWeight: 700 }}>{lead.customer_name}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                        <a
                          href={`tel:${lead.customer_phone}`}
                          onClick={(e) => e.stopPropagation()}
                          style={{ fontSize: '12px', color: 'var(--text-secondary)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '3px' }}
                        >
                          <Phone size={11} /> {lead.customer_phone}
                        </a>
                        {lead.customer_phone && (
                          <a
                            href={`https://wa.me/91${lead.customer_phone.replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            style={{ color: '#25D366' }}
                          >
                            <MessageCircle size={13} />
                          </a>
                        )}
                      </div>
                    </td>
                    <td>{getCategoryBadge(lead.lead_category)}</td>
                    <td>
                      <p style={{ fontSize: '12px', margin: 0, fontWeight: 500, color: 'var(--text-primary)' }}>
                        {lead.requirement}
                      </p>
                      {lead.property_title && (
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                          Prop: {lead.property_title}
                        </div>
                      )}
                    </td>
                    <td>
                      <div style={{ fontWeight: 500, fontSize: '12px' }}>{lead.city || 'N/A'}</div>
                      {lead.budget && (
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                          {formatIndianCurrency(lead.budget)}
                        </div>
                      )}
                    </td>
                    <td>{getPriorityBadge(lead.priority)}</td>
                    <td>
                      <span style={{ fontSize: '12px', fontWeight: 600, color: lead.assigned_to_name ? 'var(--primary)' : 'var(--text-muted)' }}>
                        {lead.assigned_to_name || 'Unassigned'}
                      </span>
                    </td>
                    <td>
                      <span style={{
                        fontSize: '11px',
                        fontWeight: 700,
                        padding: '2px 8px',
                        borderRadius: '4px',
                        backgroundColor: 'var(--bg-secondary)',
                        border: '1px solid var(--border-primary)'
                      }}>
                        {lead.status}
                      </span>
                    </td>
                    <td>
                      <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                        {new Date(lead.created_at).toLocaleDateString()}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => handleSelectLead(lead)}
                        className="btn btn-secondary btn-sm"
                        style={{ fontSize: '11px', padding: '4px 8px' }}
                      >
                        Manage Lead
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : (
        /* ================= FOLLOW-UPS & REMINDERS VIEW ================= */
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
          <div style={{ backgroundColor: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-primary)', padding: '16px' }}>
            <h3 style={{ fontSize: '15px', fontWeight: 700, marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Clock size={16} color="#ef4444" /> Scheduled Calls & Follow-ups
            </h3>
            {todaysFollowups.length === 0 ? (
              <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>No pending follow-ups for today.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {todaysFollowups.map(f => (
                  <div
                    key={f.id}
                    style={{
                      backgroundColor: 'var(--bg-primary)',
                      border: '1px solid var(--border-primary)',
                      borderRadius: 'var(--radius-sm)',
                      padding: '12px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '13px' }}>{f.task_title}</div>
                      <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '3px' }}>
                        Due: {new Date(f.due_date).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleCompleteFollowup(f.id)}
                      className="btn btn-secondary btn-sm"
                      style={{ fontSize: '11px' }}
                    >
                      <CheckCircle size={14} color="#10B981" /> Done
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ================= LEAD DETAIL SLIDE-OVER DRAWER ================= */}
      {selectedLead && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.45)',
          zIndex: 100,
          display: 'flex',
          justifyContent: 'flex-end',
          backdropFilter: 'blur(2px)'
        }}>
          <div style={{
            width: '100%',
            maxWidth: '520px',
            backgroundColor: 'var(--bg-primary)',
            height: '100%',
            overflowY: 'auto',
            boxShadow: 'var(--shadow-2xl)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Drawer Header */}
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid var(--border-primary)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: 'var(--bg-secondary)'
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <h2 style={{ fontSize: '16px', fontWeight: 700, margin: 0 }}>
                    {selectedLead.customer_name}
                  </h2>
                  {getPriorityBadge(selectedLead.priority)}
                </div>
                <div style={{ marginTop: '4px' }}>
                  {getCategoryBadge(selectedLead.lead_category)}
                </div>
              </div>

              <button
                type="button"
                onClick={() => handleSelectLead(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Quick Contact Bar */}
            <div style={{
              padding: '12px 20px',
              borderBottom: '1px solid var(--border-primary)',
              display: 'flex',
              gap: '10px'
            }}>
              <a
                href={`tel:${selectedLead.customer_phone}`}
                className="btn btn-secondary btn-sm"
                style={{ flex: 1, textDecoration: 'none', justifyContent: 'center' }}
              >
                <Phone size={14} /> Call ({selectedLead.customer_phone})
              </a>
              {selectedLead.customer_phone && (
                <a
                  href={`https://wa.me/91${selectedLead.customer_phone.replace(/\D/g, '')}?text=${encodeURIComponent(`Hi ${selectedLead.customer_name}, thank you for your enquiry with NOBROKERAGE regarding ${selectedLead.requirement}.`)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-sm"
                  style={{ flex: 1, backgroundColor: '#25D366', color: '#fff', textDecoration: 'none', justifyContent: 'center', border: 'none' }}
                >
                  <MessageCircle size={14} /> WhatsApp
                </a>
              )}
            </div>

            {/* Drawer Body */}
            <div style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Lead Information Card */}
              <div style={{
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border-primary)',
                padding: '14px'
              }}>
                <div style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '10px' }}>
                  Requirement Overview
                </div>
                <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 8px 0' }}>
                  {selectedLead.requirement}
                </p>
                {selectedLead.details && selectedLead.details !== selectedLead.requirement && (
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic', margin: '0 0 10px 0', lineHeight: '1.4' }}>
                    "{selectedLead.details}"
                  </p>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px', borderTop: '1px solid var(--border-primary)', paddingTop: '8px' }}>
                  <div>
                    <span style={{ color: 'var(--text-muted)' }}>City:</span> <strong>{selectedLead.city || 'N/A'}</strong>
                  </div>
                  <div>
                    <span style={{ color: 'var(--text-muted)' }}>Budget:</span> <strong>{selectedLead.budget ? formatIndianCurrency(selectedLead.budget) : 'N/A'}</strong>
                  </div>
                  <div>
                    <span style={{ color: 'var(--text-muted)' }}>Channel:</span> {selectedLead.source_channel}
                  </div>
                  <div>
                    <span style={{ color: 'var(--text-muted)' }}>Submitted:</span> {new Date(selectedLead.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>

              {/* CRM Pipeline Controls */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '4px' }}>
                    Pipeline Stage
                  </label>
                  <select
                    className="filter-select"
                    style={{ width: '100%' }}
                    value={selectedLead.status || 'New'}
                    onChange={(e) => handleUpdateStatus(selectedLead.lead_id, selectedLead.source_table, e.target.value)}
                  >
                    {CRM_STAGES.map(st => (
                      <option key={st.key} value={st.key}>{st.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '4px' }}>
                    Lead Priority
                  </label>
                  <select
                    className="filter-select"
                    style={{ width: '100%' }}
                    value={selectedLead.priority || 'Warm'}
                    onChange={(e) => handleUpdatePriority(selectedLead.lead_id, selectedLead.source_table, e.target.value)}
                  >
                    {CRM_PRIORITIES.map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>

                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '4px' }}>
                    Assigned Staff / Sales Executive
                  </label>
                  <select
                    className="filter-select"
                    style={{ width: '100%' }}
                    value={selectedLead.assigned_to || ''}
                    onChange={(e) => handleAssignLead(selectedLead.lead_id, selectedLead.source_table, e.target.value)}
                  >
                    <option value="">Unassigned</option>
                    {staffList.map(s => (
                      <option key={s.id} value={s.id}>{s.full_name} ({s.role})</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Add Activity / Call Note */}
              <div style={{ borderTop: '1px solid var(--border-primary)', paddingTop: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>
                  Log Activity / Call Notes
                </div>
                <form onSubmit={handleAddNote}>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <select
                      className="filter-select"
                      value={activityType}
                      onChange={(e) => setActivityType(e.target.value)}
                      style={{ fontSize: '11px', width: '130px' }}
                    >
                      <option value="call">Phone Call</option>
                      <option value="whatsapp">WhatsApp</option>
                      <option value="visit_scheduled">Site Visit</option>
                      <option value="note">Internal Note</option>
                    </select>
                  </div>

                  <textarea
                    rows={2}
                    placeholder="Enter call outcome, customer interest, or notes..."
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    className="input-field"
                    style={{ width: '100%', marginBottom: '8px', fontSize: '12px' }}
                  />

                  <button
                    type="submit"
                    disabled={savingNote || !newNote.trim()}
                    className="btn btn-primary btn-sm"
                    style={{ width: '100%', gap: '6px' }}
                  >
                    <Send size={13} /> {savingNote ? 'Saving...' : 'Add Note to Timeline'}
                  </button>
                </form>
              </div>

              {/* Schedule Follow-up Reminder */}
              <div style={{ borderTop: '1px solid var(--border-primary)', paddingTop: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>
                  Schedule Next Follow-up
                </div>
                <form onSubmit={handleCreateFollowup}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                    <input
                      type="text"
                      placeholder="Task e.g. Site Visit"
                      value={newFollowupTitle}
                      onChange={(e) => setNewFollowupTitle(e.target.value)}
                      className="input-field"
                      style={{ fontSize: '12px' }}
                      required
                    />
                    <input
                      type="datetime-local"
                      value={newFollowupDate}
                      onChange={(e) => setNewFollowupDate(e.target.value)}
                      className="input-field"
                      style={{ fontSize: '12px' }}
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={savingFollowup || !newFollowupTitle.trim() || !newFollowupDate}
                    className="btn btn-secondary btn-sm"
                    style={{ width: '100%', gap: '6px' }}
                  >
                    <Plus size={13} /> {savingFollowup ? 'Scheduling...' : 'Set Reminder'}
                  </button>
                </form>
              </div>

              {/* Timeline / Activities Feed */}
              <div style={{ borderTop: '1px solid var(--border-primary)', paddingTop: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '10px' }}>
                  Activity Timeline
                </div>

                {activitiesLoading ? (
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Loading timeline...</p>
                ) : leadActivities.length === 0 ? (
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>No activities logged yet.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {leadActivities.map(act => (
                      <div
                        key={act.id}
                        style={{
                          backgroundColor: 'var(--bg-secondary)',
                          borderRadius: 'var(--radius-sm)',
                          padding: '10px 12px',
                          borderLeft: '3px solid var(--primary)'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                          <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--primary)' }}>
                            {act.activity_type.replace('_', ' ')}
                          </span>
                          <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                            {new Date(act.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                          </span>
                        </div>
                        <p style={{ fontSize: '12px', margin: '0 0 4px 0', color: 'var(--text-primary)', lineHeight: '1.4' }}>
                          {act.notes}
                        </p>
                        <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                          By: {act.performer_name || 'Staff'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrmLeads;

